import React, {
  useCallback, useState, Fragment, ReactElement,
} from 'react';
import Card from '../card/card.component';
import Button from '../button/button.component';
import FlexContainer from '../flex-container/flex-container.component';
import ModalLayout from '../modal-layout/modal-layout.component';
import ModalLabel from '../modal-label/modal-label.component';
import ModalFieldBlock from '../modal-field-block/modal-field-block.component';
import ModalButtonBlock from '../modal-button-block/modal-button-block.component';
import FieldNumber from '../field-number/field-number.component';
import ErrorBlock from '../error-block/error-block.component';
import Image from '../image/image.component';
import Loader from '../loader/loader.component';
import StatusBar from '../status-bar/status-bar.component';
import ActionButton from '../action-button/action-button.component';
import HashBlock from '../hash-block/hash-block.component';
import CopyIcon from '../../icons/copy-icon';
import ScreenService from '../../services/screen-service';
import useScreenMobile from '../../hooks/use-screen-mobile';

const ConnectOtpModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isOpen,
    errorMessage,
    otpSecretHash,
    otpSecretQrCodeLink,
    onCloseModal,
    onVerifyOtpSecretConfirm,
  } = props;

  const isMobileScreen: boolean = useScreenMobile();

  const isLoading: boolean = !otpSecretHash || !otpSecretQrCodeLink;

  const [verificationCode, setVerificationCode]: [string | null, Function] = useState(null);
  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');

  const verifyOtpSecretConfirm: MouseEventHandlerFunction = useCallback((): void => {
    if (onVerifyOtpSecretConfirm) {
      onVerifyOtpSecretConfirm(verificationCode, (): void => {
        setVerificationCode(null);
      });
    }
  }, [verificationCode]);

  const onCodeFieldChange: Function = useCallback((value: string): void => {
    setVerificationCode(value);
  }, []);

  const onCopySecretHash: MouseEventHandlerFunction = useCallback(async (): Promise<void> => {
    if (otpSecretHash) {
      await ScreenService.copyToClipboard(otpSecretHash);
      setStatusMessage('Copied to clipboard');
      setIsOpenMessage(true);
    }
  }, [otpSecretHash]);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  return (
    <ModalLayout isOpen={isOpen} title="Setup Authenticator App" onCloseModal={onCloseModal}>
      <Card isContainer>
        {
          isLoading && (
            <Loader isInline />
          )
        }
        {
          !isLoading && (
            <Fragment>
              <ModalLabel>
                Using your Authenticator App (Google Authenticator, Microsoft Authenticator, Duo Mobile or Twilio Authy)
                {' '}
                scan this QR code
                {' '}
                or manually enter the following code:
              </ModalLabel>
              <FlexContainer isColumn={isMobileScreen}>
                <ModalLabel>
                  <Image
                    isDefault
                    text="Authenticator App QR Code"
                    link={otpSecretQrCodeLink}
                  />
                </ModalLabel>
                <ModalLabel>
                  <HashBlock>
                    {otpSecretHash}
                  </HashBlock>
                  {
                    !!otpSecretHash && (
                      <FlexContainer>
                        <ActionButton withPopup onClick={onCopySecretHash}>
                          <CopyIcon />
                        </ActionButton>
                      </FlexContainer>
                    )
                  }
                </ModalLabel>
              </FlexContainer>
              <ModalLabel>
                And then enter 6-digit authentication code here:
              </ModalLabel>
              <ModalFieldBlock isDefault>
                <FieldNumber
                  isPlain
                  isBordered
                  withNoCommas
                  withNoAutocomplete
                  isShort={!isMobileScreen}
                  value={verificationCode}
                  onChange={onCodeFieldChange}
                />
              </ModalFieldBlock>
            </Fragment>
          )
        }
      </Card>
      <ModalButtonBlock>
        <Button onClick={onCloseModal}>
          Cancel
        </Button>
        <Button isActive onClick={verifyOtpSecretConfirm}>
          Confirm
        </Button>
      </ModalButtonBlock>
      {
        !!errorMessage && (
          <ErrorBlock errorMessage={errorMessage} />
        )
      }
      <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
    </ModalLayout>
  );
});

export default ConnectOtpModal;
